import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getTodayRaces, getUpcomingRaces, getMonthRaces } from '../services/api';
import '../styles/fonts.css';
import '../styles/races.css';

function getDateKey(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

function formatTime(iso) {
  if (!iso) return '–';
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
}

function formatDateLabel(dateKey, todayStr, t, locale) {
  if (dateKey === todayStr) return t('races.today');
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (dateKey === yesterday.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })) return t('races.yesterday');
  const [day, month, year] = dateKey.split('/');
  const d = new Date(`${year}-${month}-${day}`);
  const monthName = d.toLocaleString(locale, { month: 'short' }).replace('.', '');
  const dayName = d.toLocaleString(locale, { weekday: 'short' }).replace('.', '');
  return `${day} ${monthName} (${dayName})`;
}

// "dd/mm/yyyy" → "yyyy-mm-dd"
function dateKeyToInput(key) {
  const [d, m, y] = key.split('/');
  return `${y}-${m}-${d}`;
}

// "yyyy-mm-dd" → "dd/mm/yyyy"
function inputToDateKey(val) {
  const [y, m, d] = val.split('-');
  return `${d}/${m}/${y}`;
}

// "dd/mm/yyyy" → { year, month (1-based), day }
function parseDateKey(key) {
  const [d, m, y] = key.split('/').map(Number);
  return { year: y, month: m, day: d };
}

function mergeSessions(sessions) {
  const seen = new Set();
  const result = [];
  for (const s of sessions) {
    const id = s.league_session_id;
    if (!seen.has(id)) { seen.add(id); result.push(s); }
  }
  return result;
}

function SessionRow({ s, todayStr }) {
  const { t } = useTranslation();
  const isToday = getDateKey(s.start_time) === todayStr;
  const isPast = !isToday && new Date(s.start_time) < new Date();
  const track = s.config_name && s.config_name !== s.track_name
    ? `${s.track_name} – ${s.config_name}`
    : s.track_name;

  let statusLabel = null;
  let statusCls = 'rrc-status-badge rrc-status-result';
  if (s.subsession_id) {
    statusLabel = t('common.result');
    statusCls = 'rrc-status-badge rrc-status-result';
  } else if (isToday) {
    statusLabel = t('races.live');
    statusCls = 'rrc-status-badge rrc-status-live';
  } else if (isPast) {
    statusLabel = t('races.awaiting');
    statusCls = 'rrc-status-badge rrc-status-pending';
  } else {
    statusLabel = t('races.scheduled');
    statusCls = 'rrc-status-badge rrc-status-scheduled';
  }

  let to = null;
  let href = null;
  if (s.subsession_id) to = `/races/${s.subsession_id}`;
  else if (isToday) to = `/sessions/${s.league_session_id}/live`;
  else if (!isPast && s.broadcast_url) href = s.broadcast_url;

  const catKey = s.track_category ? s.track_category.toLowerCase().replace(' ', '-') : null;

  const cardBody = (
    <div className="rrc-inner">
      <div className="rrc-main-row">
        <div className="rrc-time">{formatTime(s.start_time)}</div>
        {catKey && (
          <span className={`rrc-cat-badge track-cat-badge track-cat-${catKey}`}>
            {s.track_category}
          </span>
        )}
        <div className="rrc-info">
          <span className="rrc-league">{s.league_name}</span>
          <span className="rrc-sep">›</span>
          <span className="rrc-champ">{s.championship_name}</span>
          {s.series_name && <span className="rrc-series">{s.series_name}</span>}
        </div>
        <span className={statusCls}>{statusLabel}</span>
      </div>
      <div className="rrc-track-row">
        <span className="rrc-track">{track}</span>
      </div>
    </div>
  );

  const cls = 'rrc';
  if (to) return <Link to={to} className={cls}>{cardBody}</Link>;
  if (href) return <a href={href} className={cls} target="_blank" rel="noreferrer">{cardBody}</a>;
  return <article className={cls}>{cardBody}</article>;
}

function MonthCalendar({ availableDates, selectedDate, todayStr, onSelect, calendarMonth, onPrevMonth, onNextMonth }) {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
  const { year, month } = calendarMonth; // month 1-based

  const dayNames = useMemo(() => (
    // Jan 7 2024 = Sunday, so 7+0..7+6 = Sun..Sat
    Array.from({ length: 7 }, (_, i) =>
      new Date(2024, 0, 7 + i).toLocaleString(locale, { weekday: 'narrow' })
    )
  ), [locale]);

  const monthLabel = useMemo(() => {
    const raw = new Date(year, month - 1, 1).toLocaleString(locale, { month: 'long' });
    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }, [locale, year, month]);

  // Build set of dd/mm/yyyy keys for this month that have races
  const raceDays = useMemo(() => {
    const set = new Set();
    for (const key of availableDates) {
      const p = parseDateKey(key);
      if (p.year === year && p.month === month) set.add(p.day);
    }
    return set;
  }, [availableDates, year, month]);

  const firstDayOfMonth = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(year, month, 0).getDate();

  const selectedParsed = selectedDate ? parseDateKey(inputToDateKey(selectedDate)) : null;
  const todayParsed = parseDateKey(todayStr);

  const cells = [];
  // blank cells before day 1
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  function handleDayClick(day) {
    const key = `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
    if (raceDays.has(day)) onSelect(dateKeyToInput(key));
  }

  return (
    <div className="month-calendar">
      {/* Header */}
      <div className="cal-nav">
        <button onClick={onPrevMonth} className="cal-nav-btn">‹</button>
        <span className="cal-month-label">{monthLabel} {year}</span>
        <button onClick={onNextMonth} className="cal-nav-btn">›</button>
      </div>

      {/* Day names */}
      <div className="cal-grid">
        {dayNames.map((d, i) => (
          <div key={i} className="cal-day-name">{d}</div>
        ))}

        {cells.map((day, i) => {
          if (!day) return <div key={`blank-${i}`} />;
          const hasRace = raceDays.has(day);
          const isToday = todayParsed.year === year && todayParsed.month === month && todayParsed.day === day;
          const isSelected = selectedParsed && selectedParsed.year === year && selectedParsed.month === month && selectedParsed.day === day;

          let cls = 'cal-day';
          if (isSelected) cls += ' cal-day--selected';
          else if (isToday) cls += ' cal-day--today';
          if (hasRace) cls += ' cal-day--has-race';
          if (!hasRace) cls += ' cal-day--no-race';

          return (
            <div
              key={day}
              className={cls}
              onClick={() => handleDayClick(day)}
            >
              {day}
              {hasRace && !isSelected && <span className="cal-dot" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Races() {
  const { t, i18n } = useTranslation();
  usePageTitle('Races');
  useCanonicalTag();
  useMetaTags({
    title: 'Races | Celeritas',
    description: 'Upcoming and recent iRacing league races. Follow your favorite championships and leagues.',
    url: 'https://celeritas.live/races',
  });
  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';

  const [loading, setLoading] = useState(true);
  const [allSessions, setAllSessions] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const todayStr = useMemo(() => new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' }), []);
  const todayParsed = useMemo(() => parseDateKey(todayStr), [todayStr]);
  const [calendarMonth, setCalendarMonth] = useState({ year: todayParsed.year, month: todayParsed.month });

  const fetchedMonths = useRef(new Set());

  function monthKey(year, month) {
    return `${year}-${String(month).padStart(2, '0')}`;
  }

  const { groups, availableDates } = useMemo(() => {
    const map = {};
    const order = [];
    for (const s of allSessions) {
      const key = getDateKey(s.start_time);
      if (!map[key]) { map[key] = []; order.push(key); }
      map[key].push(s);
    }
    order.sort((a, b) => {
      const [da, ma, ya] = a.split('/');
      const [db, mb, yb] = b.split('/');
      return new Date(`${ya}-${ma}-${da}`) - new Date(`${yb}-${mb}-${db}`);
    });
    const groups = order.map(k => ({ key: k, label: formatDateLabel(k, todayStr, t, locale), sessions: map[k] }));
    return { groups, availableDates: order };
  }, [allSessions, todayStr, t, locale]);

  // Initial fetch: today + upcoming + current month in parallel
  useEffect(() => {
    const { year, month } = todayParsed;
    const mk = monthKey(year, month);
    fetchedMonths.current.add(mk);
    Promise.all([getTodayRaces(), getUpcomingRaces(), getMonthRaces(year, month)])
      .then(([today, upcoming, monthSessions]) => {
        const merged = mergeSessions([...today, ...upcoming, ...monthSessions]);
        setAllSessions(merged);
        // set initial selectedDate
        const keys = [...new Set(merged.map(s => getDateKey(s.start_time)))];
        if (keys.includes(todayStr)) {
          setSelectedDate(dateKeyToInput(todayStr));
        } else if (keys.length > 0) {
          const todayDate = new Date();
          const closest = keys.reduce((prev, curr) => {
            const [d1, m1, y1] = prev.split('/');
            const [d2, m2, y2] = curr.split('/');
            return Math.abs(new Date(`${y1}-${m1}-${d1}`) - todayDate) < Math.abs(new Date(`${y2}-${m2}-${d2}`) - todayDate) ? prev : curr;
          });
          setSelectedDate(dateKeyToInput(closest));
          const p = parseDateKey(closest);
          setCalendarMonth({ year: p.year, month: p.month });
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [todayStr, todayParsed]);

  // Fetch new month data when calendarMonth changes
  useEffect(() => {
    const { year, month } = calendarMonth;
    const mk = monthKey(year, month);
    if (fetchedMonths.current.has(mk)) return;
    fetchedMonths.current.add(mk);
    getMonthRaces(year, month)
      .then(sessions => {
        setAllSessions(prev => mergeSessions([...prev, ...sessions]));
      })
      .catch(console.error);
  }, [calendarMonth]);

  const filteredGroups = useMemo(() => {
    let filtered = groups;
    if (selectedDate) {
      const key = inputToDateKey(selectedDate);
      filtered = filtered.filter(g => g.key === key);
    }
    if (selectedCategory) {
      filtered = filtered
        .map(g => ({ ...g, sessions: g.sessions.filter(s => s.track_category?.toLowerCase() === selectedCategory.toLowerCase()) }))
        .filter(g => g.sessions.length > 0);
    }
    return filtered;
  }, [groups, selectedDate, selectedCategory]);

  function prevMonth() {
    setCalendarMonth(prev => {
      const m = prev.month === 1 ? 12 : prev.month - 1;
      const y = prev.month === 1 ? prev.year - 1 : prev.year;
      return { year: y, month: m };
    });
  }

  function nextMonth() {
    setCalendarMonth(prev => {
      const m = prev.month === 12 ? 1 : prev.month + 1;
      const y = prev.month === 12 ? prev.year + 1 : prev.year;
      return { year: y, month: m };
    });
  }

  return (
    <div className="RacesPage">
      <Header />
      <main>
        <div style={{ maxWidth: '1200px', margin: '20px auto', padding: '0 16px' }}>
          <div className="races-layout">

            {/* Left column: race list */}
            <section className="races-list-col">
              <div className="box-header" style={{ marginBottom: '12px' }}>
                <h3>{t('races.title')}</h3>
              </div>

              {loading ? (
                <p style={{ color: '#9ba3ab', padding: '20px 0' }}>{t('common.loading')}</p>
              ) : filteredGroups.length === 0 ? (
                <p style={{ color: '#9ba3ab', padding: '20px 0' }}>{t('races.noRaces')}</p>
              ) : (
                filteredGroups.map(({ key, label, sessions }) => (
                  <div key={key} style={{ marginBottom: '20px' }}>
                    <div className="date-header-compact">{label}</div>
                    <div className="rrc-list">
                      {sessions.map(s => (
                        <SessionRow key={s.league_session_id} s={s} todayStr={todayStr} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </section>

            {/* Right column: calendar */}
            <aside className="races-calendar-col">
              <MonthCalendar
                availableDates={availableDates}
                selectedDate={selectedDate}
                todayStr={todayStr}
                onSelect={setSelectedDate}
                calendarMonth={calendarMonth}
                onPrevMonth={prevMonth}
                onNextMonth={nextMonth}
              />
              <div style={{ marginTop: '14px' }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#6e7681', marginBottom: '6px', letterSpacing: '0.5px' }}>
                  {t('common.trackType')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {[null, 'Road', 'Oval', 'Dirt'].map(cat => {
                    const isActive = selectedCategory === cat;
                    return (
                      <button
                        key={cat ?? 'all'}
                        onClick={() => setSelectedCategory(cat)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          background: isActive ? '#3e4a57' : 'none',
                          border: `1px solid ${isActive ? '#6e7681' : '#3e4a57'}`,
                          borderRadius: '4px', padding: '5px 8px',
                          color: isActive ? '#fff' : '#9ba3ab', fontSize: '12px',
                          cursor: 'pointer', textAlign: 'left', transition: '0.15s',
                        }}
                      >
                        {cat === null
                          ? <span style={{ fontSize: '12px' }}>{t('common.all')}</span>
                          : <span className={`track-cat-badge track-cat-${cat.toLowerCase()}`} style={{ fontSize: '8px' }}>{cat}</span>
                        }
                      </button>
                    );
                  })}
                </div>
              </div>
            </aside>

          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export default Races;
